// components/RfiImageTools.jsx — pictures on RFI questions.
//   ImageMarkup     mark up a cut from the drawing / an uploaded or pasted picture: box, arrow, cloud, pen, text
//   RfiImage        shows a stored picture (fetched with the login token — <img src> can't send it)
//   uploadRfiImage  shrink (≤ 1800 px, JPEG) and save it on a question
//   imagesForPrint  every picture of an RFI as data URLs, for the printed RFI / PDF

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "X-Tenant-ID": localStorage.getItem("tenantId"),
});
const cls = (...c) => c.filter(Boolean).join(" ");
const fileId = (ref) => String(ref || "").replace(/^gridfs:/, "");

// ── image helpers ────────────────────────────────────────────────────────────
export const loadImage = (src) =>
  new Promise((ok, bad) => {
    const im = new Image();
    im.onload = () => ok(im);
    im.onerror = () => bad(new Error("Couldn't read that image"));
    im.src = src;
  });
export const blobToDataUrl = (blob) =>
  new Promise((ok, bad) => {
    const r = new FileReader();
    r.onload = () => ok(r.result);
    r.onerror = bad;
    r.readAsDataURL(blob);
  });
/** any canvas → JPEG blob no bigger than max px on its long side */
export async function canvasToJpeg(canvas, { max = 1800, quality = 0.85 } = {}) {
  const k = Math.min(1, max / Math.max(canvas.width, canvas.height));
  let c = canvas;
  if (k < 1) {
    c = document.createElement("canvas");
    c.width = Math.round(canvas.width * k);
    c.height = Math.round(canvas.height * k);
    const g = c.getContext("2d");
    g.imageSmoothingQuality = "high";
    g.drawImage(canvas, 0, 0, c.width, c.height);
  }
  const blob = await new Promise((ok) => c.toBlob(ok, "image/jpeg", quality));
  return { blob, w: c.width, h: c.height };
}

// ── stored pictures (auth fetch → object URL, cached for the session) ────────
const cache = new Map(); // file → Promise<objectURL>
export function storedImageUrl(file) {
  if (!cache.has(file))
    cache.set(
      file,
      axios
        .get(`${API}/rfis/images/${fileId(file)}`, { headers: authHeaders(), responseType: "blob" })
        .then((r) => URL.createObjectURL(r.data))
        .catch((e) => {
          cache.delete(file);
          throw e;
        }),
    );
  return cache.get(file);
}
export function RfiImage({ file, className, alt = "", onClick }) {
  const [url, setUrl] = useState(null);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    let live = true;
    storedImageUrl(file)
      .then((u) => live && setUrl(u))
      .catch(() => live && setBad(true));
    return () => {
      live = false;
    };
  }, [file]);
  if (bad) return <div className={cls("grid place-items-center bg-black/30 text-[10px] text-gray-500", className)}>image unavailable</div>;
  if (!url) return <div className={cls("animate-pulse bg-white/5", className)} />;
  return <img src={url} alt={alt} onClick={onClick} className={className} draggable={false} />;
}

export async function uploadRfiImage(rfiId, itemId, blob, meta = {}) {
  const params = new URLSearchParams({ itemId, ...Object.fromEntries(Object.entries(meta).filter(([, v]) => v != null && v !== "")) });
  const r = await axios.post(`${API}/rfis/${rfiId}/images?${params}`, blob, {
    headers: { ...authHeaders(), "Content-Type": blob.type || "image/jpeg" },
  });
  return r.data.rfi;
}
export const deleteRfiImage = (rfiId, imageId) =>
  axios.delete(`${API}/rfis/${rfiId}/images/${imageId}`, { headers: authHeaders() }).then((r) => r.data.rfi);
export const captionRfiImage = (rfiId, imageId, caption) =>
  axios.patch(`${API}/rfis/${rfiId}/images/${imageId}`, { caption }, { headers: authHeaders() }).then((r) => r.data.rfi);

/** { [file]: dataURL } for every picture on the RFI (for the printout) */
export async function imagesForPrint(rfi) {
  const out = {};
  for (const im of (rfi.items || []).flatMap((i) => i.images || [])) {
    try {
      const url = await storedImageUrl(im.file);
      out[im.file] = await blobToDataUrl(await (await fetch(url)).blob());
    } catch {
      /* left out of the print */
    }
  }
  return out;
}

// a picture from a paste / drop / file input → data URL (null if it isn't one)
export async function pictureFrom(fileOrItem) {
  const f = fileOrItem?.getAsFile ? fileOrItem.getAsFile() : fileOrItem;
  if (!f || !/^image\//.test(f.type)) return null;
  return blobToDataUrl(f);
}

// ═════════════════════════════════════════════════════════════════════════════
// MARK-UP
// ═════════════════════════════════════════════════════════════════════════════
const TOOLS = [
  ["rect", "▭", "Box"],
  ["cloud", "☁", "Cloud"],
  ["arrow", "↗", "Arrow"],
  ["pen", "✎", "Pen"],
  ["text", "T", "Text"],
];
const COLORS = ["#ef4444", "#f97316", "#2563eb", "#111827"];

function drawCloud(g, x, y, w, h, lw) {
  // revision-cloud: little arcs round the box
  const r = Math.max(6, lw * 4);
  const seg = (x1, y1, x2, y2) => {
    const len = Math.hypot(x2 - x1, y2 - y1);
    const n = Math.max(1, Math.round(len / (r * 2)));
    for (let i = 0; i < n; i++) {
      const ax = x1 + ((x2 - x1) * i) / n;
      const ay = y1 + ((y2 - y1) * i) / n;
      const bx = x1 + ((x2 - x1) * (i + 1)) / n;
      const by = y1 + ((y2 - y1) * (i + 1)) / n;
      const mx = (ax + bx) / 2;
      const my = (ay + by) / 2;
      const ang = Math.atan2(by - ay, bx - ax);
      g.arc(mx, my, Math.hypot(bx - ax, by - ay) / 2, ang + Math.PI, ang, false);
    }
  };
  const L = Math.min(x, x + w), T = Math.min(y, y + h), R = Math.max(x, x + w), B = Math.max(y, y + h);
  g.beginPath();
  seg(L, T, R, T);
  seg(R, T, R, B);
  seg(R, B, L, B);
  seg(L, B, L, T);
  g.stroke();
}
function paint(g, s) {
  g.strokeStyle = s.color;
  g.fillStyle = s.color;
  g.lineWidth = s.lw;
  g.lineCap = "round";
  g.lineJoin = "round";
  if (s.tool === "rect") g.strokeRect(s.x, s.y, s.w, s.h);
  else if (s.tool === "cloud") drawCloud(g, s.x, s.y, s.w, s.h, s.lw);
  else if (s.tool === "arrow") {
    const { x, y, x2, y2 } = s;
    const a = Math.atan2(y2 - y, x2 - x);
    const head = Math.max(14, s.lw * 5);
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x2, y2);
    g.stroke();
    g.beginPath();
    g.moveTo(x2, y2);
    g.lineTo(x2 - head * Math.cos(a - 0.45), y2 - head * Math.sin(a - 0.45));
    g.lineTo(x2 - head * Math.cos(a + 0.45), y2 - head * Math.sin(a + 0.45));
    g.closePath();
    g.fill();
  } else if (s.tool === "pen") {
    g.beginPath();
    s.pts.forEach(([px, py], i) => (i ? g.lineTo(px, py) : g.moveTo(px, py)));
    g.stroke();
  } else if (s.tool === "text") {
    const size = Math.max(14, s.lw * 7);
    g.font = `700 ${size}px Arial, sans-serif`;
    const w = g.measureText(s.text).width;
    g.fillStyle = "rgba(255,255,255,.85)";
    g.fillRect(s.x - 4, s.y - size, w + 8, size + 8);
    g.fillStyle = s.color;
    g.fillText(s.text, s.x, s.y);
  }
}

/**
 * <ImageMarkup src={dataURL} title onCancel onSave={({ blob, w, h, caption }) => …}>{extra controls}</ImageMarkup>
 */
export function ImageMarkup({ src, title = "Mark up the picture", sub = "", onCancel, onSave, saving = false, children, saveLabel = "Add to RFI", z = "z-[2100]" }) {
  const [img, setImg] = useState(null);
  const [tool, setTool] = useState("cloud");
  const [color, setColor] = useState(COLORS[0]);
  const [shapes, setShapes] = useState([]);
  const [draft, setDraft] = useState(null);
  const [caption, setCaption] = useState("");
  const [err, setErr] = useState("");
  const cv = useRef(null);
  useEffect(() => {
    loadImage(src).then(setImg).catch((e) => setErr(e.message));
  }, [src]);
  const lw = img ? Math.max(3, Math.round(Math.max(img.width, img.height) / 320)) : 3;
  useEffect(() => {
    const c = cv.current;
    if (!c || !img) return;
    c.width = img.width;
    c.height = img.height;
    const g = c.getContext("2d");
    g.drawImage(img, 0, 0);
    for (const s of [...shapes, ...(draft ? [draft] : [])]) paint(g, s);
  }, [img, shapes, draft]);
  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") onCancel?.();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && e.target.tagName !== "INPUT") {
        e.preventDefault();
        setShapes((s) => s.slice(0, -1));
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onCancel]);

  const at = (e) => {
    const r = cv.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * cv.current.width, ((e.clientY - r.top) / r.height) * cv.current.height];
  };
  const down = (e) => {
    if (!img) return;
    const [x, y] = at(e);
    if (tool === "text") {
      const text = window.prompt("Text on the picture:");
      if (text) setShapes((s) => [...s, { tool, color, lw, x, y, text }]);
      return;
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDraft(tool === "pen" ? { tool, color, lw, pts: [[x, y]] } : tool === "arrow" ? { tool, color, lw, x, y, x2: x, y2: y } : { tool, color, lw, x, y, w: 0, h: 0 });
  };
  const move = (e) => {
    if (!draft) return;
    const [x, y] = at(e);
    setDraft((d) => (d.tool === "pen" ? { ...d, pts: [...d.pts, [x, y]] } : d.tool === "arrow" ? { ...d, x2: x, y2: y } : { ...d, w: x - d.x, h: y - d.y }));
  };
  const up = () => {
    if (!draft) return;
    const tiny = draft.tool === "pen" ? draft.pts.length < 2 : draft.tool === "arrow" ? Math.hypot(draft.x2 - draft.x, draft.y2 - draft.y) < 6 : Math.abs(draft.w) < 6 || Math.abs(draft.h) < 6;
    if (!tiny) setShapes((s) => [...s, draft]);
    setDraft(null);
  };
  const save = async () => {
    try {
      const out = await canvasToJpeg(cv.current);
      onSave({ ...out, caption: caption.trim() });
    } catch (e) {
      setErr(e.message || "Couldn't save the picture");
    }
  };

  return (
    <div className={cls("fixed inset-0 flex items-start justify-center bg-black/75 p-4 pt-[4vh]", z)} onMouseDown={(e) => e.target === e.currentTarget && onCancel?.()}>
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl">
        <div className="flex flex-wrap items-center gap-2 border-b border-white/10 px-4 py-2.5">
          <div className="mr-2 min-w-0">
            <div className="text-sm font-semibold text-white">{title}</div>
            {sub && <div className="truncate text-[11px] text-gray-500">{sub}</div>}
          </div>
          <div className="flex rounded-lg bg-black/30 p-0.5">
            {TOOLS.map(([k, icon, label]) => (
              <button
                key={k}
                title={label}
                onClick={() => setTool(k)}
                className={cls("min-w-[34px] rounded-md px-2 py-1 text-sm", tool === k ? "bg-orange-500 text-white" : "text-gray-300 hover:bg-white/10")}
              >
                {icon}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)} title={c} className={cls("h-6 w-6 rounded-full border-2", color === c ? "border-white" : "border-transparent")} style={{ background: c }} />
            ))}
          </div>
          <button onClick={() => setShapes((s) => s.slice(0, -1))} disabled={!shapes.length} className="rounded-lg bg-white/5 px-2.5 py-1 text-xs text-gray-300 hover:bg-white/10 disabled:opacity-30">
            ↶ Undo
          </button>
          <button onClick={() => setShapes([])} disabled={!shapes.length} className="rounded-lg bg-white/5 px-2.5 py-1 text-xs text-gray-300 hover:bg-white/10 disabled:opacity-30">
            Clear
          </button>
          <button onClick={onCancel} className="ml-auto rounded-lg px-2 py-1 text-gray-400 hover:bg-white/10 hover:text-white" title="Close (Esc)">
            ✕
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-black/40 p-3">
          {err && <p className="p-6 text-center text-sm text-red-300">{err}</p>}
          <canvas
            ref={cv}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            className="mx-auto block max-h-[62vh] max-w-full cursor-crosshair touch-none rounded bg-white object-contain shadow-lg"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 border-t border-white/10 px-4 py-3">
          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption (optional) — e.g. Corridor 1904: wall type not tagged"
            className="min-w-[16rem] flex-1 rounded-lg border border-gray-700 bg-gray-950 px-2.5 py-1.5 text-[13px] text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
          />
          {children}
          <button onClick={onCancel} className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15">
            Cancel
          </button>
          <button onClick={save} disabled={!img || saving} className="rounded-lg bg-orange-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-40">
            {saving ? "Saving…" : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
